import { useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { CALENDAR_ROUTE, CALENDAR_TRIAL_ROUTE } from '../../../app/router/routes'
import { useAppSelector } from '../../../app/store/store'
import { calendarService, ICalendarFilter, IGetCalendarFilterOptionsResponse } from '../../../entities/calendar'
import { FEATURES, FEATURE_SECTIONS, hasFeature, useMyActions } from '../../../entities/my'
import { CalendarFilters, getCalendarFilterFromSearchParams, getSearchParamsFromCalendarFilter,
    isCalendarFilterComplete } from '../../../features/calendarFilters'
import { getSearchParamsFromTrialCalendarFilter, getTrialCalendarFilterFromSearchParams,
    TrialCalendarFilters } from '../../../features/trialCalendarFilters'
import { useGlobalMessageActions } from '../../../entities/globalMessage'
import { AuthError } from '../../../shared/lib/error/AuthError'
import { MyError } from '../../../shared/lib/error/MyError'
import { FilterIcon } from '../../../shared/ui/icon'
import { SwitchButton } from '../../../shared/ui/switchButton'
import classes from './calendarLessonsLayout.module.scss'

const TUTOR_LESSON_FILTER: ICalendarFilter = {
    tgAccountIds: [],
    userIds: [],
    studentIds: [],
    tagIds: [],
    includeCancelled: true,
    includeRescheduled: true,
}

const normalizeFilterOptions = (
    response: IGetCalendarFilterOptionsResponse
): IGetCalendarFilterOptionsResponse => ({
    viewer: response.viewer,
    tgAccounts: response.tgAccounts || [],
    tutors: response.tutors || [],
    students: response.students || [],
    tags: response.tags || [],
})

const isAbortError = (error: unknown): boolean => {
    return error instanceof DOMException && error.name === 'AbortError'
}

const getFilterFromOptions = (options: IGetCalendarFilterOptionsResponse | null): ICalendarFilter => {
    if(!options) return TUTOR_LESSON_FILTER

    return {
        tgAccountIds: [],
        userIds: options.tutors.map(tutor => tutor.userId),
        studentIds: [],
        tagIds: [],
        includeCancelled: true,
        includeRescheduled: true,
    }
}

export default function CalendarLessonsLayoutPage() {

    const [searchParams, setSearchParams] = useSearchParams()
    const {my} = useAppSelector(state => state.myReducer)
    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const navigate = useNavigate()
    const location = useLocation()
    const currentPathname = location.pathname.replace(/\/$/, '')
    const isTrialCalendar = currentPathname.startsWith(CALENDAR_TRIAL_ROUTE.path)
    const canViewLessonCalendar = hasFeature(my, FEATURE_SECTIONS.CALENDAR)
    const canViewTrialCalendar = hasFeature(my, FEATURE_SECTIONS.TRIAL_LESSONS)
        && hasFeature(my, FEATURES.TRIAL_LESSONS.CALENDAR_READ)
    const searchParamsKey = searchParams.toString()
    const lessonSearchRef = useRef<string>(isTrialCalendar ? '' : searchParamsKey)
    const trialSearchRef = useRef<string>(isTrialCalendar ? searchParamsKey : '')
    const [validatedFilterKey, setValidatedFilterKey] = useState<string | null>(null)
    const [validatedFilterOptions, setValidatedFilterOptions] = useState<IGetCalendarFilterOptionsResponse | null>(null)
    const [trialFilterOptions, setTrialFilterOptions] = useState<IGetCalendarFilterOptionsResponse | null>(null)
    const [isDayContentOpen, setIsDayContentOpen] = useState(false)
    const filterFromUrl = useMemo(
        () => getCalendarFilterFromSearchParams(new URLSearchParams(searchParamsKey)),
        [searchParamsKey]
    )
    const filterFromUrlKey = useMemo(
        () => getSearchParamsFromCalendarFilter(filterFromUrl).toString(),
        [filterFromUrl]
    )
    const trialFilter = useMemo(
        () => getTrialCalendarFilterFromSearchParams(new URLSearchParams(searchParamsKey)),
        [searchParamsKey]
    )
    const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(() => !isCalendarFilterComplete(filterFromUrl))
    const canSwitchCalendars = canViewLessonCalendar && canViewTrialCalendar
    const shouldUseTutorLessonCalendar = !isTrialCalendar && my.role === 'tutor'
    const tutorLessonFilter = useMemo(
        () => getFilterFromOptions(validatedFilterOptions),
        [validatedFilterOptions]
    )
    const lessonFilter = shouldUseTutorLessonCalendar ? tutorLessonFilter : filterFromUrl
    const lessonFilterKey = shouldUseTutorLessonCalendar
        ? getSearchParamsFromCalendarFilter(tutorLessonFilter).toString()
        : filterFromUrlKey
    const canShowLessonCalendar = shouldUseTutorLessonCalendar
        ? Boolean(validatedFilterOptions)
        : filterFromUrlKey === validatedFilterKey
            && isCalendarFilterComplete(filterFromUrl)
            && Boolean(validatedFilterOptions)
    const canShowOutlet = isTrialCalendar
        ? canViewTrialCalendar
        : canViewLessonCalendar && canShowLessonCalendar
    const outletKey = isTrialCalendar
        ? CALENDAR_TRIAL_ROUTE.path
        : `${currentPathname}-${lessonFilterKey}`

    useEffect(() => {
        if(!isTrialCalendar){
            lessonSearchRef.current = shouldUseTutorLessonCalendar ? '' : searchParamsKey
        }
        else{
            trialSearchRef.current = searchParamsKey
        }
    }, [isTrialCalendar, searchParamsKey, shouldUseTutorLessonCalendar])

    const selectCalendar = (selected: 1 | 2) => {
        const nextIsTrialCalendar = selected === 2

        if(nextIsTrialCalendar === isTrialCalendar) return

        setIsFiltersOpen(nextIsTrialCalendar || my.role !== 'tutor')
        navigate(
            {
                pathname: nextIsTrialCalendar ? CALENDAR_TRIAL_ROUTE.path : CALENDAR_ROUTE.path,
                search: nextIsTrialCalendar ? trialSearchRef.current : lessonSearchRef.current,
            },
            {replace: true}
        )
    }

    useEffect(() => {
        if(!shouldUseTutorLessonCalendar){
            return
        }

        const controller = new AbortController()
        const filterKey = getSearchParamsFromCalendarFilter(TUTOR_LESSON_FILTER).toString()

        setIsFiltersOpen(false)
        setValidatedFilterKey(null)
        setValidatedFilterOptions(null)

        calendarService.getCalendarFilterOptions({selected: TUTOR_LESSON_FILTER}, controller.signal)
            .then(response => {
                if(controller.signal.aborted) return

                setValidatedFilterKey(filterKey)
                setValidatedFilterOptions(normalizeFilterOptions(response))
            })
            .catch(error => {
                if(isAbortError(error)) return

                console.log(error)
                setValidatedFilterKey(null)
                setValidatedFilterOptions(null)

                if(error instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else if(error instanceof MyError){
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при получении фильтров', type: 'error'})
                }
            })

        return () => {
            controller.abort()
        }
    }, [setGlobalMessage, setIsAuth, shouldUseTutorLessonCalendar])

    return (
        <>
            {(canSwitchCalendars || (!isDayContentOpen && !shouldUseTutorLessonCalendar)) && (
            <section className={classes.controls}>
                {canSwitchCalendars && (
                    <section className={classes.calendarSwitch}>
                        <SwitchButton
                            text1="Обычные занятия"
                            text2="Пробные занятия"
                            selected={isTrialCalendar ? 2 : 1}
                            onSelected={selectCalendar}
                        />
                    </section>
                )}

                {!isDayContentOpen && !shouldUseTutorLessonCalendar && <button
                    type="button"
                    className={`${classes.filtersButton} ${isFiltersOpen ? classes.open : ''}`}
                    onClick={() => setIsFiltersOpen(value => !value)}
                >
                    <FilterIcon size={18} />
                    Фильтры
                </button>}
            </section>
            )}

            {!shouldUseTutorLessonCalendar && <section className={`${classes.filtersPanel} ${isFiltersOpen ? classes.open : ''} ${isDayContentOpen ? classes.hidden : ''}`}>
                {isTrialCalendar ? (
                    <TrialCalendarFilters
                        filter={trialFilter}
                        onChange={filter => {
                            const nextSearchParams = getSearchParamsFromTrialCalendarFilter(filter)

                            if(currentPathname !== CALENDAR_TRIAL_ROUTE.path){
                                navigate({
                                    pathname: CALENDAR_TRIAL_ROUTE.path,
                                    search: nextSearchParams.toString(),
                                }, {replace: true})
                                return
                            }

                            setSearchParams(nextSearchParams, {replace: true})
                        }}
                        onOptionsChange={setTrialFilterOptions}
                    />
                ) : (
                    <CalendarFilters
                        filter={filterFromUrl}
                        onChange={(filter, changedField) => {
                            const nextSearchParams = getSearchParamsFromCalendarFilter(filter)
                            const isDisplayFilter = changedField === 'includeCancelled'
                                || changedField === 'includeRescheduled'

                            if(currentPathname !== CALENDAR_ROUTE.path && !isDisplayFilter){
                                navigate(
                                    {
                                        pathname: CALENDAR_ROUTE.path,
                                        search: nextSearchParams.toString(),
                                    },
                                    {replace: true}
                                )
                                return
                            }

                            setSearchParams(nextSearchParams, {replace: true})
                        }}
                        onValidated={(filter, options) => {
                            setValidatedFilterKey(filter ? getSearchParamsFromCalendarFilter(filter).toString() : null)
                            setValidatedFilterOptions(filter ? options ?? null : null)
                        }}
                    />
                )}
            </section>}

            {canShowOutlet && (
                <Outlet
                    key={outletKey}
                    context={{
                        filter: lessonFilter,
                        filterOptions: validatedFilterOptions,
                        trialFilter,
                        trialFilterOptions,
                        setIsDayContentOpen,
                    }}
                />
            )}
        </>
    )
}
